import { useEffect, useMemo, useState } from 'react';

import { isAdminRole, useAuth } from './auth';
import { LoadingIndicator } from './components/LoadingIndicator';
import { MonthWednesdayStepper } from './components/MonthWednesdayStepper';
import { PageHero } from './components/PageHero';
import {
  formatDate,
  getUpcomingWednesday,
  parseIsoDateUtc,
  toLocalIsoDate,
  weekdayLabel,
} from './lib/dates';
import {
  fetchMeta,
  fetchPeople,
  fetchPersonSlots,
  fetchPlanForDate,
  fetchTradeSlots,
  personIcsUrl,
  slotIcsUrl,
  swapSlots,
  updateTradeStatus,
  ApiError,
  type ApiMeta,
  type PersonSummary,
  type PlanSlot,
} from './lib/api';

type TopTab = 'plan' | 'tauschmarkt' | 'person';
type MobileTradeStep = 'select' | 'review';

const today = toLocalIsoDate(new Date());
const upcomingWednesday = getUpcomingWednesday(new Date());
const selectedMemberStorageKey = 'stimmbildung.selectedMemberId';

function readStoredSelectedPersonId(): number | null {
  try {
    const storedValue = window.localStorage.getItem(selectedMemberStorageKey);

    if (!storedValue) {
      return null;
    }

    const personId = Number(storedValue);
    return Number.isInteger(personId) && personId > 0 ? personId : null;
  } catch {
    return null;
  }
}

function storeSelectedPersonId(personId: number | null) {
  try {
    if (personId === null) {
      window.localStorage.removeItem(selectedMemberStorageKey);
      return;
    }

    window.localStorage.setItem(selectedMemberStorageKey, String(personId));
  } catch {
    // localStorage can be unavailable in private or restricted browser modes.
  }
}

function sortByDateAndTime(a: PlanSlot, b: PlanSlot): number {
  if (a.appointmentDate !== b.appointmentDate) {
    return a.appointmentDate.localeCompare(b.appointmentDate);
  }

  return a.startsAt.localeCompare(b.startsAt);
}

function isGroupTrainingSlot(slot: PlanSlot): boolean {
  return slot.slotType === 'group_training';
}

function slotAssignmentLabel(slot: PlanSlot, emptyLabel = 'unbesetzt'): string {
  return slot.slotLabel ?? slot.currentPersonName ?? emptyLabel;
}

function slotOriginalAssignmentLabel(
  slot: PlanSlot,
  emptyLabel = 'unbesetzt',
): string {
  return slot.slotLabel ?? slot.originalPersonName ?? emptyLabel;
}

function slotStatus(slot: PlanSlot): string {
  if (isGroupTrainingSlot(slot)) {
    return 'Gruppenstimmbildung';
  }

  if (slot.currentPersonId === null) {
    return 'offen';
  }

  if (slot.wantsTrade) {
    return 'TAUSCHWUNSCH';
  }

  return 'normal';
}

function slotStatusClassName(slot: PlanSlot): string {
  if (isGroupTrainingSlot(slot)) {
    return 'status-group';
  }

  if (slot.currentPersonId === null) {
    return 'status-free';
  }

  if (slot.wantsTrade) {
    return 'status-trade';
  }

  return 'status-normal';
}

function IcsIcon() {
  return (
    <svg
      aria-hidden="true"
      className="ics-icon"
      focusable="false"
      viewBox="0 0 24 24"
    >
      <path d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1.5A2.5 2.5 0 0 1 22 6.5v12A2.5 2.5 0 0 1 19.5 21h-15A2.5 2.5 0 0 1 2 18.5v-12A2.5 2.5 0 0 1 4.5 4H6V3a1 1 0 0 1 1-1Zm12.5 8h-15v8.5a.5.5 0 0 0 .5.5h14a.5.5 0 0 0 .5-.5V10ZM12 12a1 1 0 0 1 1 1v2.6l.8-.8a1 1 0 0 1 1.4 1.4l-2.5 2.5a1 1 0 0 1-1.4 0l-2.5-2.5a1 1 0 1 1 1.4-1.4l.8.8V13a1 1 0 0 1 1-1ZM5 6a1 1 0 0 0-1 1v1h16V7a1 1 0 0 0-1-1H5Z" />
    </svg>
  );
}

function IcsLink({
  children,
  className = 'button-link',
  href,
}: {
  children: string;
  className?: string;
  href: string;
}) {
  return (
    <a className={`${className} ics-link`} href={href}>
      <IcsIcon />
      <span>{children}</span>
    </a>
  );
}

function ShareIcon() {
  return (
    <svg
      aria-hidden="true"
      className="ics-icon"
      focusable="false"
      viewBox="0 0 24 24"
    >
      <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81a3 3 0 1 0-3-3c0 .24.04.47.09.7L8.04 9.81A3 3 0 1 0 6 15a3 3 0 0 0 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65a2.92 2.92 0 1 0 2.92-2.92Z" />
    </svg>
  );
}

function SlotStatusBadge({ slot }: { slot: PlanSlot }) {
  if (!slot.wantsTrade) {
    return null;
  }

  return (
    <span className={`status-badge ${slotStatusClassName(slot)}`}>
      Tauschwunsch
    </span>
  );
}

function hasDifferentOriginalAssignment(slot: PlanSlot): boolean {
  return slot.originalPersonId !== slot.currentPersonId;
}

function slotTimeRange(slot: PlanSlot): string {
  return `${slot.startsAt}-${slot.endsAt}`;
}

function slotDateTimeLabel(slot: PlanSlot): string {
  return `${formatDate(slot.appointmentDate)} · ${slotTimeRange(slot)}`;
}

function buildTradeShareMessage(
  slot: PlanSlot,
  personName: string | null,
): string {
  const weekday = weekdayLabel(slot.appointmentDate);
  const dateLabel = formatDate(slot.appointmentDate);
  const signature = personName ? `\n\nViele Grüße\n${personName}` : '';

  return (
    `Hallo zusammen,\n\n` +
    `ich möchte gerne meinen Stimmbildungstermin tauschen:\n` +
    `📅 ${weekday}, ${dateLabel}\n` +
    `🕒 ${slotTimeRange(slot)} Uhr` +
    signature
  );
}

async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall back to the legacy approach below.
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const succeeded = document.execCommand('copy');
    document.body.removeChild(textarea);
    return succeeded;
  } catch {
    return false;
  }
}

export default function App() {
  const { user } = useAuth();
  const canAccessAdmin = isAdminRole(user?.role);
  const [meta, setMeta] = useState<ApiMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [personSlotsLoading, setPersonSlotsLoading] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);

  const [topTab, setTopTab] = useState<TopTab>('person');
  const [selectedDate, setSelectedDate] = useState(upcomingWednesday);

  const [people, setPeople] = useState<PersonSummary[]>([]);
  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(() =>
    readStoredSelectedPersonId(),
  );
  const [showIdentityDialog, setShowIdentityDialog] = useState(false);
  const [identityDraftPersonId, setIdentityDraftPersonId] = useState<
    number | ''
  >('');
  const [includePast, setIncludePast] = useState(false);
  const [tradeIncludePast, setTradeIncludePast] = useState(false);

  const [dateSlots, setDateSlots] = useState<PlanSlot[]>([]);
  const [personSlots, setPersonSlots] = useState<PlanSlot[]>([]);
  const [tradeSlots, setTradeSlots] = useState<PlanSlot[]>([]);

  const [selectedOfferSlotId, setSelectedOfferSlotId] = useState<number | null>(
    null,
  );
  const [selectedOwnSlotId, setSelectedOwnSlotId] = useState<number | null>(
    null,
  );
  const [showSwapDialog, setShowSwapDialog] = useState(false);
  const [swapError, setSwapError] = useState<string | null>(null);

  const [shareSlot, setShareSlot] = useState<PlanSlot | null>(null);
  const [shareCopied, setShareCopied] = useState(false);

  const [isMobile, setIsMobile] = useState<boolean>(
    () => window.innerWidth < 900,
  );
  const [mobileTradeStep, setMobileTradeStep] =
    useState<MobileTradeStep>('select');

  useEffect(() => {
    const media = window.matchMedia('(max-width: 899px)');
    const update = () => setIsMobile(media.matches);
    update();
    media.addEventListener('change', update);

    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    storeSelectedPersonId(selectedPersonId);
  }, [selectedPersonId]);

  useEffect(() => {
    setPersonSlots([]);
    setSelectedOfferSlotId(null);
    setSelectedOwnSlotId(null);
    setShowSwapDialog(false);
    setSwapError(null);
    setMobileTradeStep('select');
  }, [selectedPersonId]);

  useEffect(() => {
    let active = true;

    async function loadBaseData() {
      setLoading(true);

      try {
        const [metaPayload, peoplePayload, datePayload, tradePayload] =
          await Promise.all([
            fetchMeta(),
            fetchPeople(),
            fetchPlanForDate(selectedDate),
            fetchTradeSlots(),
          ]);

        if (!active) {
          return;
        }

        setMeta(metaPayload);
        setPeople(peoplePayload.items);
        setDateSlots([...datePayload.items].sort(sortByDateAndTime));
        setTradeSlots([...tradePayload.items].sort(sortByDateAndTime));

        setSelectedPersonId((current) => {
          if (current === null) {
            return null;
          }

          return peoplePayload.items.some((person) => person.id === current)
            ? current
            : null;
        });
        setError(null);
      } catch {
        if (active) {
          setError('Backend nicht erreichbar');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    void loadBaseData();

    return () => {
      active = false;
    };
  }, [selectedDate]);

  useEffect(() => {
    let active = true;

    async function loadPersonData() {
      if (!selectedPersonId) {
        setPersonSlots([]);
        setPersonSlotsLoading(false);
        return;
      }

      setPersonSlotsLoading(true);

      try {
        const payload = await fetchPersonSlots(selectedPersonId, true);

        if (!active) {
          return;
        }

        setPersonSlots([...payload.items].sort(sortByDateAndTime));
      } catch {
        if (active) {
          setError('Termine konnten nicht geladen werden');
        }
      } finally {
        if (active) {
          setPersonSlotsLoading(false);
        }
      }
    }

    void loadPersonData();

    return () => {
      active = false;
    };
  }, [selectedPersonId]);

  const selectedPerson = useMemo(() => {
    if (!selectedPersonId) {
      return null;
    }

    return people.find((person) => person.id === selectedPersonId) ?? null;
  }, [people, selectedPersonId]);
  const selectedPersonName = selectedPerson?.displayName ?? null;
  const identityDialogForced =
    !loading && people.length > 0 && selectedPerson === null;
  const identityDialogOpen = showIdentityDialog || identityDialogForced;

  useEffect(() => {
    if (identityDialogOpen) {
      setIdentityDraftPersonId(selectedPerson?.id ?? '');
    }
  }, [identityDialogOpen, selectedPerson]);

  const ownTradableSlots = useMemo(() => {
    if (!selectedPersonId) {
      return [];
    }

    return personSlots.filter(
      (slot) =>
        !isGroupTrainingSlot(slot) &&
        slot.currentPersonId === selectedPersonId &&
        slot.currentPersonId !== null &&
        slot.originalPersonId !== null &&
        (tradeIncludePast || slot.appointmentDate >= today),
    );
  }, [personSlots, selectedPersonId, tradeIncludePast]);

  const offerSlots = useMemo(() => {
    const base = tradeIncludePast
      ? tradeSlots
      : tradeSlots.filter((slot) => slot.appointmentDate >= today);

    if (!selectedPersonId) {
      return base;
    }

    return base.filter((slot) => slot.currentPersonId !== selectedPersonId);
  }, [selectedPersonId, tradeSlots, tradeIncludePast]);

  const selectedOfferSlot = useMemo(
    () => offerSlots.find((slot) => slot.id === selectedOfferSlotId) ?? null,
    [offerSlots, selectedOfferSlotId],
  );

  const selectedOwnSlot = useMemo(
    () =>
      ownTradableSlots.find((slot) => slot.id === selectedOwnSlotId) ?? null,
    [ownTradableSlots, selectedOwnSlotId],
  );

  useEffect(() => {
    if (selectedOfferSlotId === null) {
      return;
    }

    if (!offerSlots.some((slot) => slot.id === selectedOfferSlotId)) {
      setSelectedOfferSlotId(null);
    }
  }, [offerSlots, selectedOfferSlotId]);

  const personUpcomingSlots = useMemo(
    () => personSlots.filter((slot) => slot.appointmentDate >= today),
    [personSlots],
  );

  const personVisibleSlots = useMemo(
    () =>
      includePast
        ? personSlots
        : personSlots.filter((slot) => slot.appointmentDate >= today),
    [personSlots, includePast],
  );

  function canToggleTradeForSlot(slot: PlanSlot): boolean {
    return (
      selectedPersonId !== null &&
      !isGroupTrainingSlot(slot) &&
      slot.currentPersonId === selectedPersonId
    );
  }

  function isOwnSlot(slot: PlanSlot): boolean {
    return (
      selectedPersonId !== null && slot.currentPersonId === selectedPersonId
    );
  }

  function isEmphasizedSlot(slot: PlanSlot): boolean {
    return isGroupTrainingSlot(slot) || isOwnSlot(slot);
  }

  function handleIdentitySave() {
    if (identityDraftPersonId === '') {
      return;
    }

    setSelectedPersonId(identityDraftPersonId);
    setShowIdentityDialog(false);
  }

  async function refreshCurrentViews() {
    const [datePayload, tradePayload] = await Promise.all([
      fetchPlanForDate(selectedDate),
      fetchTradeSlots(),
    ]);

    setDateSlots([...datePayload.items].sort(sortByDateAndTime));
    setTradeSlots([...tradePayload.items].sort(sortByDateAndTime));

    if (selectedPersonId) {
      const personPayload = await fetchPersonSlots(selectedPersonId, true);
      setPersonSlots([...personPayload.items].sort(sortByDateAndTime));
    }
  }

  async function handleToggleTrade(slot: PlanSlot) {
    setBusyAction(`trade-${slot.id}`);

    try {
      await updateTradeStatus(slot.id, !slot.wantsTrade);
      await refreshCurrentViews();
      setError(null);
    } catch {
      setError('Tauschwunsch konnte nicht aktualisiert werden');
    } finally {
      setBusyAction(null);
    }
  }

  async function handleSwapConfirmed() {
    if (!selectedOfferSlot || !selectedOwnSlot) {
      return;
    }

    setBusyAction('swap');
    setSwapError(null);

    try {
      await swapSlots(selectedOfferSlot, selectedOwnSlot);
      await refreshCurrentViews();
      setSelectedOfferSlotId(null);
      setSelectedOwnSlotId(null);
      setShowSwapDialog(false);
      setMobileTradeStep('select');
      setError(null);
    } catch (caughtError) {
      const isConflict =
        caughtError instanceof ApiError && caughtError.status === 409;
      const detail =
        caughtError instanceof ApiError && caughtError.message
          ? caughtError.message
          : 'Der Tausch konnte nicht ausgeführt werden.';

      setSwapError(
        isConflict ? detail : `${detail} Bitte versuche es später erneut.`,
      );
    } finally {
      setBusyAction(null);
    }
  }

  async function handleRefreshSwapData() {
    setBusyAction('swap-refresh');

    try {
      await refreshCurrentViews();
      setSwapError(null);
      setError(null);
      // Return to the (now up-to-date) selection view. The offered slot may no
      // longer be available, in which case the review/confirmation has nothing
      // to show – so we always go back to the market overview.
      setShowSwapDialog(false);
      setMobileTradeStep('select');
    } catch {
      setSwapError(
        'Daten konnten nicht aktualisiert werden. Bitte lade die Seite neu.',
      );
    } finally {
      setBusyAction(null);
    }
  }

  function handleOpenMobileTradeReview() {
    setSwapError(null);
    setMobileTradeStep('review');
    window.requestAnimationFrame(() => window.scrollTo({ top: 0 }));
  }

  function handleOpenShareDialog(slot: PlanSlot) {
    setShareSlot(slot);
    setShareCopied(false);
  }

  function handleCloseShareDialog() {
    setShareSlot(null);
    setShareCopied(false);
  }

  async function handleCopyShareMessage(message: string) {
    const copied = await copyTextToClipboard(message);

    if (copied) {
      setShareCopied(true);
      setError(null);
    } else {
      setShareCopied(false);
      setError('Nachricht konnte nicht kopiert werden');
    }
  }

  async function handleNativeShare(message: string) {
    try {
      await navigator.share({ text: message });
    } catch {
      // The user dismissed the share sheet or sharing is unavailable.
    }
  }

  function renderIdentityDialog() {
    if (!identityDialogOpen) {
      return null;
    }

    return (
      <div className="identity-modal-backdrop">
        <section
          className="identity-modal-card"
          role="dialog"
          aria-modal="true"
          aria-labelledby="identity-dialog-title"
        >
          <p className="eyebrow">Das bin ich</p>
          <h2 id="identity-dialog-title">Mitglied auswählen</h2>
          <p className="muted">
            Bitte wähle einmal aus, welches Mitglied du bist. Diese Auswahl wird
            lokal in deinem Browser gespeichert und für deine Termine sowie den
            Tauschmarkt verwendet.
          </p>

          <label className="field">
            <span>Das bin ich</span>
            <select
              autoFocus
              value={identityDraftPersonId}
              onChange={(event) =>
                setIdentityDraftPersonId(
                  event.target.value ? Number(event.target.value) : '',
                )
              }
            >
              <option value="">Bitte wählen</option>
              {people.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.displayName}
                </option>
              ))}
            </select>
          </label>

          {people.length === 0 ? (
            <p className="muted">Es sind noch keine Mitglieder angelegt.</p>
          ) : null}

          <div className="actions identity-modal-actions">
            {!identityDialogForced ? (
              <button
                type="button"
                className="button-secondary"
                onClick={() => setShowIdentityDialog(false)}
              >
                Abbrechen
              </button>
            ) : null}
            <button
              type="button"
              className="button-primary"
              disabled={identityDraftPersonId === ''}
              onClick={handleIdentitySave}
            >
              Speichern
            </button>
          </div>
        </section>
      </div>
    );
  }

  function renderTopNavigation() {
    const items: Array<{ id: TopTab; label: string }> = [
      { id: 'person', label: 'Meine Termine' },
      { id: 'plan', label: 'Plan' },
      { id: 'tauschmarkt', label: 'Tauschmarkt' },
    ];

    return (
      <nav className="public-top-nav" aria-label="Hauptnavigation">
        {items.map((item) => {
          return (
            <button
              key={item.id}
              type="button"
              className={`public-tab ${topTab === item.id ? 'active' : ''}`}
              onClick={() => setTopTab(item.id)}
            >
              {item.label}
            </button>
          );
        })}
      </nav>
    );
  }

  function renderDayView() {
    return (
      <section className="panel public-panel">
        <div className="panel-head public-panel-head plan-panel-head">
          <div className="panel-title-group plan-panel-title">
            <p className="eyebrow">Plan &gt; Tagesansicht</p>
          </div>
          <div className="plan-controls">
            <MonthWednesdayStepper
              date={selectedDate}
              onChange={setSelectedDate}
            />
          </div>
        </div>

        {loading ? (
          <LoadingIndicator label="Plan wird geladen …" />
        ) : isMobile ? (
          <div className="mobile-slot-list">
            {dateSlots.length === 0 ? (
              <p className="muted empty-state">keine Stimmbildung geplant</p>
            ) : null}
            {dateSlots.map((slot) => (
              <article
                key={slot.id}
                className={`slot-card person-slot-card mobile-slot-card mobile-plan-slot-card${
                  isEmphasizedSlot(slot) ? ' slot-emphasized' : ''
                }`}
              >
                <div className="slot-card-header">
                  <div className="slot-card-title">
                    <strong className="slot-time">
                      {slot.startsAt}-{slot.endsAt}
                    </strong>
                    <span>{slotAssignmentLabel(slot)}</span>
                  </div>
                  <SlotStatusBadge slot={slot} />
                </div>

                {hasDifferentOriginalAssignment(slot) ? (
                  <p className="slot-original-note">
                    Ursprünglich für{' '}
                    <strong>{slotOriginalAssignmentLabel(slot)}</strong>
                  </p>
                ) : null}

                {canToggleTradeForSlot(slot) ? (
                  <div className="slot-actions person-slot-actions">
                    <button
                      type="button"
                      className="button-secondary"
                      disabled={busyAction === `trade-${slot.id}`}
                      onClick={() => handleToggleTrade(slot)}
                    >
                      {slot.wantsTrade ? 'Tausch -' : 'Tausch +'}
                    </button>
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        ) : (
          <div className="table-wrap">
            <table className="slot-table">
              <thead>
                <tr>
                  <th>Zeit</th>
                  <th>Aktuelle Zuteilung</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {dateSlots.length === 0 ? (
                  <tr>
                    <td className="slot-table-empty" colSpan={3}>
                      keine Stimmbildung geplant
                    </td>
                  </tr>
                ) : null}
                {dateSlots.map((slot) => (
                  <tr
                    key={slot.id}
                    className={isEmphasizedSlot(slot) ? 'slot-emphasized' : ''}
                  >
                    <td className="slot-table-time">
                      {slot.startsAt}-{slot.endsAt}
                    </td>
                    <td>
                      <div className="slot-assignment-cell">
                        <strong className="slot-current-name">
                          {slotAssignmentLabel(slot, '- unbesetzt -')}
                        </strong>
                        {hasDifferentOriginalAssignment(slot) ? (
                          <p className="slot-original-note">
                            Ursprünglich für{' '}
                            <strong>{slotOriginalAssignmentLabel(slot)}</strong>
                          </p>
                        ) : null}
                      </div>
                    </td>
                    <td className="table-status-cell">
                      <div className="table-status-stack">
                        <SlotStatusBadge slot={slot} />
                        {canToggleTradeForSlot(slot) ? (
                          <div className="slot-actions table-inline-actions">
                            <button
                              type="button"
                              className="button-secondary"
                              disabled={busyAction === `trade-${slot.id}`}
                              onClick={() => handleToggleTrade(slot)}
                            >
                              {slot.wantsTrade
                                ? 'Tauschwunsch entfernen'
                                : 'Tauschwunsch setzen'}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    );
  }

  function renderPersonView() {
    const upcomingAppointmentLabel =
      personUpcomingSlots.length === 1
        ? '1 zukünftiger Termin'
        : `${personUpcomingSlots.length} zukünftige Termine`;

    return (
      <section className="panel public-panel person-panel">
        <div className="panel-head public-panel-head person-panel-head">
          <div className="panel-title-group">
            <h2>Meine Termine</h2>
            {selectedPerson ? (
              <p className="muted">
                {selectedPerson.displayName} · {upcomingAppointmentLabel}
              </p>
            ) : null}
          </div>
          {selectedPerson ? (
            <IcsLink
              className="button-secondary person-export-link"
              href={personIcsUrl(selectedPerson.id, selectedPerson.icsToken)}
            >
              Alle zukünftigen Termine herunterladen
            </IcsLink>
          ) : null}
        </div>

        <div className="person-toolbar">
          <label className="checkbox person-checkbox">
            <input
              type="checkbox"
              checked={includePast}
              onChange={(event) => setIncludePast(event.target.checked)}
            />
            Mit Vergangenheit
          </label>
          <span className="muted">
            {includePast
              ? 'Vergangene Termine werden mit angezeigt.'
              : 'Aktuell werden nur zukünftige Termine gezeigt.'}
          </span>
        </div>

        <div className="slot-stack person-slot-grid">
          {personSlotsLoading ? (
            <LoadingIndicator
              className="loading-indicator-block"
              label="Termine werden geladen …"
            />
          ) : (
            <>
              {personVisibleSlots.map((slot) => (
                <article key={slot.id} className="slot-card person-slot-card">
                  <div className="slot-card-header">
                    <div className="slot-card-title">
                      <span className="slot-date">
                        {formatDate(slot.appointmentDate)}
                      </span>
                      <strong className="slot-time">
                        {slot.startsAt}-{slot.endsAt}
                      </strong>
                    </div>
                    <SlotStatusBadge slot={slot} />
                  </div>

                  {hasDifferentOriginalAssignment(slot) ? (
                    <p className="slot-original-note">
                      Ursprünglich für{' '}
                      <strong>{slotOriginalAssignmentLabel(slot)}</strong>
                    </p>
                  ) : null}

                  <div className="slot-actions person-slot-actions">
                    <IcsLink href={slotIcsUrl(slot.id, slot.icsToken)}>
                      Kalendereintrag
                    </IcsLink>
                    {slot.wantsTrade ? (
                      <button
                        type="button"
                        className="button-secondary share-button"
                        onClick={() => handleOpenShareDialog(slot)}
                      >
                        <ShareIcon />
                        <span>Tauschwunsch teilen</span>
                      </button>
                    ) : null}
                    {canToggleTradeForSlot(slot) ? (
                      <button
                        type="button"
                        className="button-secondary"
                        disabled={busyAction === `trade-${slot.id}`}
                        onClick={() => handleToggleTrade(slot)}
                      >
                        {slot.wantsTrade ? 'Tausch -' : 'Tausch +'}
                      </button>
                    ) : null}
                  </div>
                </article>
              ))}
              {selectedPerson && personVisibleSlots.length === 0 ? (
                <p className="muted empty-state">Keine Termine gefunden.</p>
              ) : null}
            </>
          )}
        </div>
      </section>
    );
  }

  function renderTradeView() {
    const offerCountLabel =
      offerSlots.length === 1 ? '1 Gesuch' : `${offerSlots.length} Gesuche`;
    const ownCountLabel =
      ownTradableSlots.length === 1
        ? '1 eigener Termin'
        : `${ownTradableSlots.length} eigene Termine`;
    const canReviewSwap =
      selectedOfferSlot !== null &&
      selectedOwnSlot !== null &&
      selectedOfferSlot.id !== selectedOwnSlot.id;

    function renderTradeCard(
      slot: PlanSlot,
      selected: boolean,
      onSelectionChange: (selected: boolean) => void,
      variant: 'offer' | 'own',
    ) {
      const textId = `trade-${variant}-${slot.id}-text`;

      return (
        <label
          key={slot.id}
          className={`trade-card ${selected ? 'selected' : ''}`}
        >
          <input
            type="checkbox"
            className="trade-card-checkbox"
            checked={selected}
            aria-labelledby={textId}
            onChange={(event) => onSelectionChange(event.currentTarget.checked)}
          />
          <span className="trade-card-copy" id={textId}>
            {variant === 'offer' ? (
              <>
                <span className="trade-card-explanation">
                  <strong>{slotAssignmentLabel(slot)}</strong> möchte diesen
                  Termin tauschen
                </span>
                <span className="trade-card-term">
                  {slotDateTimeLabel(slot)}
                </span>
              </>
            ) : (
              <>
                <span className="trade-card-term">
                  {slotDateTimeLabel(slot)}
                </span>
                {slot.wantsTrade ? (
                  <span className="status-badge status-trade trade-card-badge">
                    Tauschwunsch
                  </span>
                ) : null}
              </>
            )}
          </span>
        </label>
      );
    }

    return (
      <section className="panel public-panel trade-panel">
        <div className="panel-head public-panel-head trade-panel-head">
          <div className="panel-title-group">
            <h2>Tauschmarkt</h2>
            <p className="muted">
              Wähle ein Gesuch und einen eigenen Termin aus, den du dafür
              anbieten möchtest.
            </p>
          </div>
        </div>

        <div className="person-toolbar trade-toolbar">
          <label className="checkbox person-checkbox">
            <input
              type="checkbox"
              checked={tradeIncludePast}
              onChange={(event) => setTradeIncludePast(event.target.checked)}
            />
            Mit Vergangenheit
          </label>
          <span className="muted">
            {tradeIncludePast
              ? 'Vergangene Termine werden mit angezeigt.'
              : 'Aktuell werden nur zukünftige Termine gezeigt.'}
          </span>
        </div>

        <div className={`trade-layout ${isMobile ? 'mobile' : ''}`}>
          <section
            className="trade-column"
            aria-labelledby="trade-offers-title"
          >
            <div className="trade-column-head">
              <h3 id="trade-offers-title">Gesuche</h3>
              <span className="trade-count-pill">{offerCountLabel}</span>
            </div>
            <div className="slot-stack trade-card-list">
              {loading ? (
                <LoadingIndicator label="Gesuche werden geladen …" />
              ) : (
                <>
                  {offerSlots.map((slot) =>
                    renderTradeCard(
                      slot,
                      selectedOfferSlotId === slot.id,
                      (checked) =>
                        setSelectedOfferSlotId(checked ? slot.id : null),
                      'offer',
                    ),
                  )}
                  {offerSlots.length === 0 ? (
                    <p className="muted trade-empty-state">
                      Aktuell gibt es keine offenen Tauschgesuche anderer
                      Mitglieder.
                    </p>
                  ) : null}
                </>
              )}
            </div>
          </section>

          <section className="trade-column" aria-labelledby="trade-own-title">
            <div className="trade-column-head">
              <h3 id="trade-own-title">Meine tauschfähigen Termine</h3>
              <span className="trade-count-pill">{ownCountLabel}</span>
            </div>
            <div className="slot-stack trade-card-list">
              {personSlotsLoading ? (
                <LoadingIndicator label="Termine werden geladen …" />
              ) : (
                <>
                  {ownTradableSlots.map((slot) =>
                    renderTradeCard(
                      slot,
                      selectedOwnSlotId === slot.id,
                      (checked) =>
                        setSelectedOwnSlotId(checked ? slot.id : null),
                      'own',
                    ),
                  )}
                  {ownTradableSlots.length === 0 ? (
                    <p className="muted trade-empty-state">
                      Du hast aktuell keine eigenen tauschfähigen Termine.
                    </p>
                  ) : null}
                </>
              )}
            </div>
          </section>
        </div>

        <div className="actions trade-action-bar">
          {isMobile ? (
            <button
              type="button"
              className="button-primary"
              disabled={!canReviewSwap}
              onClick={handleOpenMobileTradeReview}
            >
              Weiter zur Prüfung
            </button>
          ) : (
            <button
              type="button"
              className="button-primary"
              disabled={!canReviewSwap}
              onClick={() => {
                setSwapError(null);
                setShowSwapDialog(true);
              }}
            >
              Tausch starten
            </button>
          )}
        </div>
      </section>
    );
  }

  function renderSwapDialog(dialogMode: 'desktop' | 'mobile') {
    if (!selectedOfferSlot || !selectedOwnSlot) {
      return null;
    }

    const dialogTitleId = `swap-dialog-title-${dialogMode}`;
    const dialogClassName =
      dialogMode === 'desktop'
        ? 'swap-dialog-card'
        : 'swap-dialog-card swap-review-card';

    function renderSwapResultRow(slot: PlanSlot, personName: string) {
      return (
        <div className="swap-outcome-row">
          <strong>{personName}</strong>
          <span>{slotDateTimeLabel(slot)}</span>
        </div>
      );
    }

    const dialogContent = (
      <section
        className={dialogClassName}
        role="dialog"
        aria-modal={dialogMode === 'desktop' ? true : undefined}
        aria-labelledby={dialogTitleId}
      >
        <header className="swap-dialog-head">
          <h3 id={dialogTitleId}>Tausch bestätigen</h3>
        </header>

        <section className="swap-outcome" aria-label="Nach dem Tausch">
          <h4>Nach dem Tausch</h4>
          <div className="swap-outcome-list">
            {renderSwapResultRow(
              selectedOfferSlot,
              slotAssignmentLabel(selectedOwnSlot, '---'),
            )}
            {renderSwapResultRow(
              selectedOwnSlot,
              slotAssignmentLabel(selectedOfferSlot, '---'),
            )}
          </div>
        </section>

        {swapError ? (
          <div className="swap-error" role="alert">
            <p className="swap-error-message">{swapError}</p>
            <p className="swap-error-hint">
              Vermutlich wurde dieser Tausch zwischenzeitlich von jemand anderem
              durchgeführt oder die Daten sind veraltet. Aktualisiere die
              Ansicht und prüfe, ob der Tausch noch nötig ist.
            </p>
            <div className="actions swap-error-actions">
              <button
                type="button"
                className="button-secondary"
                disabled={busyAction === 'swap-refresh'}
                onClick={handleRefreshSwapData}
              >
                {busyAction === 'swap-refresh'
                  ? 'Aktualisiere...'
                  : 'Daten aktualisieren'}
              </button>
            </div>
          </div>
        ) : null}

        <div className="actions swap-actions">
          <button
            type="button"
            className="button-secondary"
            onClick={() => {
              setSwapError(null);
              if (dialogMode === 'desktop') {
                setShowSwapDialog(false);
              } else {
                setMobileTradeStep('select');
              }
            }}
          >
            Abbrechen
          </button>
          <button
            type="button"
            className="button-primary"
            disabled={busyAction === 'swap'}
            onClick={handleSwapConfirmed}
          >
            {busyAction === 'swap' ? 'Tausche...' : 'Verbindlich tauschen'}
          </button>
        </div>
      </section>
    );

    if (dialogMode === 'desktop') {
      return <div className="swap-modal">{dialogContent}</div>;
    }

    return dialogContent;
  }

  function renderShareDialog() {
    if (!shareSlot) {
      return null;
    }

    const message = buildTradeShareMessage(shareSlot, selectedPersonName);
    const canNativeShare = typeof navigator.share === 'function';

    return (
      <div className="share-modal" onClick={handleCloseShareDialog}>
        <section
          className="share-dialog-card"
          role="dialog"
          aria-modal="true"
          aria-labelledby="share-dialog-title"
          onClick={(event) => event.stopPropagation()}
        >
          <header className="share-dialog-head">
            <h3 id="share-dialog-title">Tauschwunsch teilen</h3>
            <p className="muted">
              Kopiere diese Nachricht und teile sie mit den anderen Mitgliedern,
              damit sich jemand für einen Tausch melden kann.
            </p>
          </header>

          <textarea
            className="share-message"
            readOnly
            rows={8}
            value={message}
            onFocus={(event) => event.currentTarget.select()}
          />

          <div className="actions share-actions">
            <button
              type="button"
              className="button-secondary"
              onClick={handleCloseShareDialog}
            >
              Schließen
            </button>
            {canNativeShare ? (
              <button
                type="button"
                className="button-secondary"
                onClick={() => handleNativeShare(message)}
              >
                Teilen …
              </button>
            ) : null}
            <button
              type="button"
              className="button-primary"
              onClick={() => handleCopyShareMessage(message)}
            >
              {shareCopied ? 'Kopiert ✓' : 'Nachricht kopieren'}
            </button>
          </div>
        </section>
      </div>
    );
  }

  function renderPlanTab() {
    return renderDayView();
  }

  function renderMainContent() {
    if (topTab === 'plan') {
      return renderPlanTab();
    }

    if (topTab === 'person') {
      return renderPersonView();
    }

    if (topTab === 'tauschmarkt') {
      if (
        isMobile &&
        mobileTradeStep === 'review' &&
        selectedOfferSlot &&
        selectedOwnSlot
      ) {
        return (
          <section className="panel public-panel">
            {renderSwapDialog('mobile')}
          </section>
        );
      }

      return renderTradeView();
    }
    return renderPlanTab();
  }

  return (
    <main className="layout public-layout">
      <PageHero
        canAccessAdmin={canAccessAdmin}
        eyebrow="Mitgliederbereich"
        isAdminPage={false}
        selectedMemberName={selectedPersonName}
        title={meta?.appName ?? 'Cantabile Stimmbildung'}
        onSelectedMemberClick={() => setShowIdentityDialog(true)}
      />

      {!isMobile ? renderTopNavigation() : null}
      {error ? (
        <p className="error-text public-error" role="alert">
          {error}
        </p>
      ) : null}
      {renderMainContent()}

      {showSwapDialog ? renderSwapDialog('desktop') : null}
      {renderShareDialog()}
      {renderIdentityDialog()}

      {isMobile ? (
        <nav className="mobile-bottom-nav" aria-label="Mobile Navigation">
          <button
            type="button"
            className={topTab === 'person' ? 'active' : ''}
            onClick={() => setTopTab('person')}
          >
            Meine Termine
          </button>
          <button
            type="button"
            className={topTab === 'plan' ? 'active' : ''}
            onClick={() => setTopTab('plan')}
          >
            Plan
          </button>
          <button
            type="button"
            className={topTab === 'tauschmarkt' ? 'active' : ''}
            onClick={() => setTopTab('tauschmarkt')}
          >
            Tausch
          </button>
        </nav>
      ) : null}
    </main>
  );
}
